'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { demoData, generateDemoData } from '../data/demo-data';
import { UserResponse } from '../models/core/user';
import { StudentResponse } from '../models/core/student';
import { Goal, Achievement, Career, Assessment, WeeklyProgress, AcademicPerformance, CareerPath } from '../types';


interface DataContextType {
  user: UserResponse;
  student: StudentResponse;
  goals: Goal[];
  achievements: Achievement[];
  careers: Career[];
  assessments: Assessment[];
  weeklyProgress: WeeklyProgress[];
  academicPerformance: AcademicPerformance[];
  careerPaths: CareerPath[];
  updateGoalProgress: (goalId: string, progress: number) => void;
  addGoal: (goal: Omit<Goal, 'id' | 'createdAt' | 'updatedAt'>) => void;
  unlockAchievement: (achievementId: string) => void;
  resetDemoData: () => void;
  generateNewDemoData: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setData] = useState(generateDemoData);

  const updateGoalProgress = (goalId: string, progress: number) => {
    setData(prev => ({
      ...prev,
      goals: prev.goals.map(goal => 
        goal.id === goalId 
          ? { ...goal, progress, updatedAt: new Date() }
          : goal
      ),
    }));
  };

  const addGoal = (goalData: Omit<Goal, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newGoal: Goal = {
      ...goalData,
      id: `goal_${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date(),
      icon: '🎯',
      color: '#3B82F6'
    };
    setData(prev => ({ ...prev, goals: [...prev.goals, newGoal] }));
  };

  const unlockAchievement = (achievementId: string) => {
    setData(prev => ({
      ...prev,
      achievements: prev.achievements.map(ach =>
        ach.id === achievementId && !ach.unlocked
          ? { ...ach, unlocked: true, unlockedAt: new Date() }
          : ach
      ),
    }));
  };

  const resetDemoData = () => {
    setData(generateDemoData());
  };

  const generateNewDemoData = () => {
    setData(generateDemoData());
  };

  const value: DataContextType = {
    ...data,
    updateGoalProgress,
    addGoal,
    unlockAchievement,
    resetDemoData,
    generateNewDemoData,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  console.log('DataContext:', context);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};